import React, { useEffect, useState } from 'react';
import { FaSearch, FaFilter, FaEllipsisV } from 'react-icons/fa';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import './Transactions.css';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { auth } from '../firebase/firebase';
import moment from 'moment';

// Transaction status constants
const TRANSACTION_STATUS = {
    PENDING: 'PENDING',
    WAITING_FOR_PAYMENT: 'WAITING_FOR_PAYMENT',
    PAYMENT_SUCCESS: 'PAYMENT_SUCCESS',
    WAITING_CONFIRMATION: 'WAITING_CONFIRMATION',
    WAITING_DELIVERY: 'WAITING_DELIVERY',
    IN_DELIVERY: 'IN_DELIVERY',
    DELIVERED: 'DELIVERED',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    REJECTED: 'REJECTED',
    EXPIRED: 'EXPIRED',
    CANCELLED: 'CANCELLED',
    REMOVED: 'REMOVED',
    COMPLAINT: 'COMPLAINT'
};

// Status that can be cancelled
const CANCELLABLE_STATUSES = [
    TRANSACTION_STATUS.WAITING_DELIVERY,
    TRANSACTION_STATUS.IN_DELIVERY,
    TRANSACTION_STATUS.DELIVERED
];

// Status that can be completed
const COMPLETABLE_STATUSES = [
    TRANSACTION_STATUS.DELIVERED,
    TRANSACTION_STATUS.COMPLAINT
];

// Status that can be complaint
const COMPLAINTABLE_STATUSES = [
    TRANSACTION_STATUS.COMPLAINT
];

// Terminal statuses that cannot be modified
const TERMINAL_STATUSES = [
    TRANSACTION_STATUS.COMPLETED,
    TRANSACTION_STATUS.FAILED,
    TRANSACTION_STATUS.REJECTED,
    TRANSACTION_STATUS.EXPIRED,
    TRANSACTION_STATUS.CANCELLED,
    TRANSACTION_STATUS.REMOVED
];

// Statuses that can have notes
const STATUSES_WITH_NOTES = [
    TRANSACTION_STATUS.IN_DELIVERY,
    TRANSACTION_STATUS.DELIVERED,
    TRANSACTION_STATUS.COMPLETED,
    TRANSACTION_STATUS.FAILED,
    TRANSACTION_STATUS.REJECTED,
    TRANSACTION_STATUS.EXPIRED,
    TRANSACTION_STATUS.CANCELLED
];

export default function Transactions() {
    const [filterStatus, setFilterStatus] = useState('all');
    const [transactions, setTransactions] = useState([]);
    const [showMenu, setShowMenu] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [actionType, setActionType] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        limit: 10
    });
    const [filterOptions, setFilterOptions] = useState({
        buyerId: '',
        sellerId: '',
        filterByBuyer: false,
        filterBySeller: false,
        status: '',
        startDate: '',
        endDate: ''
    });

    const fetchTransactions = async (customFilters = null) => {
        setIsLoading(true);
        try {
            const transactions = await getTransactions(pagination.currentPage, customFilters);
            setTransactions(transactions.rows || []);
            setPagination(prev => ({
                ...prev,
                totalPages: transactions.total_pages || 1,
                totalItems: transactions.total_rows || 0
            }));
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Failed to fetch transactions');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [pagination.currentPage, filterStatus]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchTransactions();
    };

    const handleTabChange = (status) => {
        setFilterStatus(status);
        // Clear any status filter when changing tabs
        setFilterOptions(prev => ({
            ...prev,
            status: ''
        }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage
            }));
        }
    };

    const handleMenuClick = (event, transactionId) => {
        event.stopPropagation();
        setShowMenu(showMenu === transactionId ? null : transactionId);
    };

    const handleAction = (transaction, action) => {
        setSelectedTransaction(transaction);
        setActionType(action);
        setShowMenu(null);
        setShowConfirmModal(true);
    };

    const handleConfirm = async () => {
        if (actionType === 'cancel') {
            setShowConfirmModal(false);
            setShowReasonModal(true);
        } else if (actionType === 'remove' || actionType === 'complete') {
            await executeAction();
        }
    };

    const handleCancel = async () => {
        if (cancelReason.trim() === '') {
            toast.error('Please provide a reason for cancellation');
            return;
        }
        await executeAction();
    };

    const executeAction = async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            const url = `${process.env.REACT_APP_API_URL}/admin/transactions/${selectedTransaction.id}/${actionType}`;
            const body = actionType === 'cancel' ? { notes: cancelReason } : undefined;

            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: body ? JSON.stringify(body) : undefined
            });

            if (!response.ok) {
                throw new Error(`Failed to ${actionType} transaction`);
            }

            toast.success(`Transaction ${actionType}d successfully`);
            setShowConfirmModal(false);
            setShowReasonModal(false);
            setCancelReason('');
            setSelectedTransaction(null);
            setActionType(null);

            // Refresh transactions list
            const updatedTransactions = await getTransactions(pagination.currentPage);
            setTransactions(updatedTransactions.rows || []);
            setPagination(prev => ({
                ...prev,
                totalPages: updatedTransactions.total_pages,
                totalItems: updatedTransactions.total_rows
            }));
        } catch (error) {
            console.error(`Error ${actionType}ing transaction:`, error);
            toast.error(`Failed to ${actionType} transaction`);
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setShowMenu(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const getStatusClass = (status) => {
        switch (status) {
            case TRANSACTION_STATUS.PENDING:
            case TRANSACTION_STATUS.WAITING_FOR_PAYMENT:
                return 'pending';
            case TRANSACTION_STATUS.PAYMENT_SUCCESS:
            case TRANSACTION_STATUS.WAITING_CONFIRMATION:
            case TRANSACTION_STATUS.WAITING_DELIVERY:
                return 'processing';
            case TRANSACTION_STATUS.IN_DELIVERY:
                return 'in_progress';
            case TRANSACTION_STATUS.DELIVERED:
            case TRANSACTION_STATUS.COMPLETED:
                return 'completed';
            case TRANSACTION_STATUS.FAILED:
            case TRANSACTION_STATUS.REJECTED:
            case TRANSACTION_STATUS.EXPIRED:
            case TRANSACTION_STATUS.CANCELLED:
            case TRANSACTION_STATUS.REMOVED:
                return 'failed';
            default:
                return '';
        }
    };

    const getStatusDisplay = (status) => {
        return status.replace(/_/g, ' ').toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const canShowActionMenu = (status) => {
        return true;
    };

    const canCancel = (status) => {
        return CANCELLABLE_STATUSES.includes(status);
    };

    const canComplete = (status) => {
        return COMPLETABLE_STATUSES.includes(status);
    };

    const canComplaint = (status) => {
        return COMPLAINTABLE_STATUSES.includes(status);
    };

    const renderStatus = (transaction) => {
        const statusClass = getStatusClass(transaction.status);
        const hasNote = STATUSES_WITH_NOTES.includes(transaction.status) && transaction.notes;
        const statusElement = (
            <span className={`status ${statusClass} ${hasNote ? 'has-note' : ''}`}>
                {getStatusDisplay(transaction.status)}
            </span>
        );

        if (hasNote) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`tooltip-${transaction.id}`}>
                            {transaction.notes}
                        </Tooltip>
                    }
                >
                    {statusElement}
                </OverlayTrigger>
            );
        }

        return statusElement;
    };

    const handleFilterApply = () => {
        // If a status is selected in filter, switch to 'all' tab
        if (filterOptions.status) {
            setFilterStatus('all');
        }
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setShowFilterModal(false);
        fetchTransactions();
    };

    const handleFilterReset = () => {
        const emptyFilters = {
            buyerId: '',
            sellerId: '',
            filterByBuyer: false,
            filterBySeller: false,
            status: '',
            startDate: '',
            endDate: ''
        };
        
        // First fetch with empty filters
        fetchTransactions(emptyFilters);
        
        // Then update the state
        setFilterOptions(emptyFilters);
        setShowFilterModal(false);
    };

    const getTransactions = async (page, customFilters = null) => {
        try {
            let url = `${process.env.REACT_APP_API_URL}/admin/transactions?page=${page}&limit=${pagination.limit}`;
            
            // Use either custom filters or current filter state
            const filtersToUse = customFilters || filterOptions;

            // Add tab status filter if not 'all'
            if (filterStatus !== 'all') {
                url += `&tab_status=${filterStatus}`;
            }
            // Add filter status only if no tab status is active and a filter status is selected
            else if (filtersToUse.status) {
                url += `&transaction_status=${filtersToUse.status}`;
            }

            // Add search query if present
            if (searchQuery.trim()) {
                url += `&transaction_id=${searchQuery.trim()}`;
            }

            // Add filter options
            if (filtersToUse.filterByBuyer && filtersToUse.buyerId.trim()) {
                url += `&buyer_id=${filtersToUse.buyerId.trim()}`;
            }
            if (filtersToUse.filterBySeller && filtersToUse.sellerId.trim()) {
                url += `&seller_id=${filtersToUse.sellerId.trim()}`;
            }
            if (filtersToUse.startDate) {
                url += `&start_date=${filtersToUse.startDate}`;
            }
            if (filtersToUse.endDate) {
                url += `&end_date=${filtersToUse.endDate}`;
            }

            const token = await auth.currentUser.getIdToken();
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            return {
                rows: data.rows || [],
                total_pages: data.total_pages || 1,
                total_rows: data.total_rows || 0
            };
        } catch (error) {
            console.error('Error fetching transactions:', error);
            return { rows: [], total_pages: 1, total_rows: 0 };
        }
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        
        // Count user filters
        if (filterOptions.filterByBuyer && filterOptions.buyerId.trim()) count++;
        if (filterOptions.filterBySeller && filterOptions.sellerId.trim()) count++;
        
        // Count status filter
        if (filterOptions.status) count++;
        
        // Count date filters
        if (filterOptions.startDate) count++;
        if (filterOptions.endDate) count++;
        
        return count;
    };

    return (
        <div className="transactions">
            <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce}
            />
            <div className="transactions-header">
                <h1>Transactions</h1>
                <div className="actions">
                    <form onSubmit={handleSearch} className="search-bar">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by transaction ID..."
                            className="search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </form>
                    <button className="filter-btn" onClick={() => setShowFilterModal(true)}>
                        <FaFilter />
                        Filter
                        {getActiveFiltersCount() > 0 && (
                            <div className='badge-filter'>{getActiveFiltersCount()}</div>
                        )}
                    </button>
                </div>
            </div>

            <div className="status-filters">
                <button
                    className={filterStatus === 'all' ? 'active' : ''}
                    onClick={() => handleTabChange('all')}
                >
                    All
                </button>
                <button
                    className={filterStatus === 'waiting_payment' ? 'active' : ''}
                    onClick={() => handleTabChange('waiting_payment')}
                >
                    Waiting for Payment
                </button>
                <button
                    className={filterStatus === 'waiting_confirmation' ? 'active' : ''}
                    onClick={() => handleTabChange('waiting_confirmation')}
                >
                    Waiting for Confirmation
                </button>
                <button
                    className={filterStatus === 'in_progress' ? 'active' : ''}
                    onClick={() => handleTabChange('in_progress')}
                >
                    In Progress
                </button>
                <button
                    className={filterStatus === 'completed' ? 'active' : ''}
                    onClick={() => handleTabChange('completed')}
                >
                    Completed
                </button>
            </div>

            <div className="transactions-table">
                {isLoading ? (
                    <div className="loading-spinner">Loading...</div>
                ) : transactions.length === 0 ? (
                    <div className="no-transactions pt-3 px-3">
                        <p>No transactions found</p>
                    </div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Transaction ID</th>
                                <th>Buyer</th>
                                <th>Seller</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Last Updated Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map(transaction => {
                                return (
                                    <tr key={transaction.id}>
                                        <td className='cursor' onClick={() => {
                                            navigator.clipboard.writeText(transaction.id);
                                            toast.success('Transaction ID copied to clipboard');
                                        }}>
                                            {transaction.id}
                                        </td>
                                        <td>
                                            <div className='d-flex flex-column'>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`tooltip-${transaction.buyer.uid}`}>
                                                            User ID: {transaction.buyer.uid}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        className="user-name cursor"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(transaction.buyer.uid);
                                                            toast.success('User ID copied to clipboard');
                                                        }}
                                                    >
                                                        {transaction.buyer.first_name} {transaction.buyer.last_name}
                                                    </span>
                                                </OverlayTrigger>
                                            </div>
                                        </td>
                                        <td>
                                            <div className='d-flex flex-column'>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`tooltip-${transaction.seller.uid}`}>
                                                            User ID: {transaction.seller.uid}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        className="user-name cursor"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(transaction.seller.uid);
                                                            toast.success('User ID copied to clipboard');
                                                        }}
                                                    >
                                                        {transaction.seller.first_name} {transaction.seller.last_name}
                                                    </span>
                                                </OverlayTrigger>
                                            </div>
                                        </td>
                                        <td>{transaction.amount.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                        <td>
                                            {renderStatus(transaction)}
                                        </td>
                                        <td>
                                            {
                                                transaction.status in TERMINAL_STATUSES ?
                                                    <span>{moment(transaction.transaction_completed_time).format('DD MMMM YYYY, HH:mm:ss')}</span>
                                                    : transaction.status in COMPLETABLE_STATUSES ?
                                                        <span>{moment(transaction.transaction_arrived_time).format('DD MMMM YYYY, HH:mm:ss')}</span>
                                                        : transaction.status === TRANSACTION_STATUS.IN_DELIVERY ?
                                                            <span>{moment(transaction.transaction_delivery_time).format('DD MMMM YYYY, HH:mm:ss')}</span>
                                                            : transaction.status === TRANSACTION_STATUS.WAITING_DELIVERY ?
                                                                <span>{moment(transaction.transaction_confirmation_time).format('DD MMMM YYYY, HH:mm:ss')}</span>
                                                                :
                                                                <span>{moment(transaction.created_at).format('DD MMMM YYYY, HH:mm:ss')}</span>
                                            }
                                        </td>
                                        <td className="action-cell">
                                            {canShowActionMenu(transaction.status) && (
                                                <>
                                                    <button
                                                        className="action-btn"
                                                        onClick={(e) => handleMenuClick(e, transaction.id)}
                                                    >
                                                        <FaEllipsisV />
                                                    </button>
                                                    {showMenu === transaction.id && (
                                                        <div className="action-menu">
                                                            {canComplete(transaction.status) && (
                                                                <button
                                                                    className="menu-item success"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAction(transaction, 'complete');
                                                                    }}
                                                                >
                                                                    Complete
                                                                </button>
                                                            )}
                                                            {canComplaint(transaction.status) && (
                                                                <button
                                                                    className="menu-item success"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAction(transaction, 'complain');
                                                                    }}
                                                                >
                                                                    Complaint
                                                                </button>
                                                            )}
                                                            {canCancel(transaction.status) && (
                                                                <button
                                                                    className="menu-item warning"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAction(transaction, 'cancel');
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            )}
                                                            <button
                                                                className="menu-item danger"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAction(transaction, 'remove');
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Filter Modal */}
            {showFilterModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Filter Transactions</h2>
                        <div className="filter-options">
                            <div className="filter-section mb-4">
                                <h3 className="mb-3">User Filter</h3>
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="filterByBuyer"
                                            checked={filterOptions.filterByBuyer}
                                            onChange={(e) => setFilterOptions(prev => ({
                                                ...prev,
                                                filterByBuyer: e.target.checked
                                            }))}
                                        />
                                        <label htmlFor="filterByBuyer">Filter by Buyer ID</label>
                                    </div>
                                    {filterOptions.filterByBuyer && (
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Buyer ID"
                                            value={filterOptions.buyerId}
                                            onChange={(e) => setFilterOptions(prev => ({
                                                ...prev,
                                                buyerId: e.target.value
                                            }))}
                                        />
                                    )}
                                    
                                    <div className="d-flex align-items-center gap-2 mt-2">
                                        <input
                                            type="checkbox"
                                            id="filterBySeller"
                                            checked={filterOptions.filterBySeller}
                                            onChange={(e) => setFilterOptions(prev => ({
                                                ...prev,
                                                filterBySeller: e.target.checked
                                            }))}
                                        />
                                        <label htmlFor="filterBySeller">Filter by Seller ID</label>
                                    </div>
                                    {filterOptions.filterBySeller && (
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Seller ID"
                                            value={filterOptions.sellerId}
                                            onChange={(e) => setFilterOptions(prev => ({
                                                ...prev,
                                                sellerId: e.target.value
                                            }))}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="filter-section mb-4">
                                <h3 className="mb-3">Status</h3>
                                <select
                                    className="form-control"
                                    value={filterOptions.status}
                                    onChange={(e) => setFilterOptions(prev => ({
                                        ...prev,
                                        status: e.target.value
                                    }))}
                                >
                                    <option value="">Select Status</option>
                                    {Object.entries(TRANSACTION_STATUS).map(([key, value]) => (
                                        <option key={key} value={value}>
                                            {value.replace(/_/g, ' ')}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-section mb-4">
                                <h3 className="mb-3">Date Range</h3>
                                <div className="d-flex gap-2">
                                    <div>
                                        <label className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={filterOptions.startDate}
                                            onChange={(e) => setFilterOptions(prev => ({
                                                ...prev,
                                                startDate: e.target.value
                                            }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={filterOptions.endDate}
                                            onChange={(e) => setFilterOptions(prev => ({
                                                ...prev,
                                                endDate: e.target.value
                                            }))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn secondary" onClick={() => setShowFilterModal(false)}>
                                Cancel
                            </button>
                            <button className="btn danger" onClick={() => {
                                handleFilterReset();
                            }}>
                                Reset
                            </button>
                            <button className="btn primary" onClick={handleFilterApply}>
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Confirm {actionType.charAt(0).toUpperCase() + actionType.slice(1)}</h2>
                        <p>Are you sure you want to {actionType} this transaction?</p>
                        <div className="modal-actions">
                            <button className="btn secondary" onClick={() => setShowConfirmModal(false)}>
                                No
                            </button>
                            <button
                                className={`btn ${actionType === 'cancel'
                                    ? 'warning'
                                    : actionType === 'complete'
                                        ? 'success'
                                        : 'danger'
                                    }`}
                                onClick={handleConfirm}
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reason Modal */}
            {showReasonModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Cancellation Reason</h2>
                        <div className="form-group">
                            <label>Please provide a reason for cancellation:</label>
                            <textarea
                                rows={3}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Enter cancellation reason..."
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn secondary" onClick={() => setShowReasonModal(false)}>
                                Back
                            </button>
                            <button className="btn warning" onClick={handleCancel}>
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="pagination">
                <button 
                    disabled={pagination.currentPage === 1}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                    Previous
                </button>
                <span>
                    Page {pagination.currentPage} of {pagination.totalPages}
                    {pagination.totalItems > 0 && ` (${pagination.totalItems} total)`}
                </span>
                <button 
                    disabled={pagination.currentPage === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                    Next
                </button>
            </div>
        </div>
    );
}
